'use client'

import { PropsWithChildren } from 'react'

import {
  ApolloProvider,
  createApolloClient,
} from '@material-bank/graphql-client'
import { getBearerToken } from '@material-bank/mtag-authentication'
import {
  createConstructorRecommendationsLink,
  prefetchConstructorRecommendations,
} from '@material-bank/mtag-client-api'
import { HapticsProvider } from '@material-bank/mtag-utilities'

const client = createApolloClient({
  uri: '/graphql/v1',
  credentials: 'include',
  getToken: getBearerToken,
  sendBatchHeader: true,
  // Operations that must not share a batch, because a batched response is emitted
  // once (Apollo Server has no incremental delivery) and holds every operation in
  // it until the slowest resolves:
  // - TagQuery: the head of the critical path. The product query cannot start
  //   until TagQuery returns the productRef (TagProviderClient feeds it to
  //   ProductProvider), and first paint waits on the product. Batched with the
  //   cart/projects/orders/user queries — none of which the product screen needs —
  //   the tag lookup was held until the slowest of the five resolved, delaying the
  //   product fetch from even beginning. Unbatched, it lands as soon as it is ready.
  // - VariantAvailability: stock is read alongside the product but nothing on
  //   screen waits for it, while the PDP cannot paint until the product lands.
  // - BrandRep: a two-hop upstream (tag lookup, then the brand-rep lookup) fired
  //   on the tag page alongside the fast TagQuery, which paints the page. Batched,
  //   the tag would wait on the rep lookup; unbatched, each lands on its own.
  // - SimilarProducts / PairsWithProducts: fired by usePrefetchDiscoveryData on
  //   the PDP's mount tick, in the same window as the PDP's own product query.
  //   A batched response only resolves once every operation in it does, so
  //   batching these with the product query would hold first paint on a
  //   "free" background prefetch — the opposite of what it's for.
  // - ConstructorUserContext: read by the Constructor link before those two
  //   queries go to Constructor from the browser, so it sits on the same path.
  unbatchedOperations: [
    'TagQuery',
    'VariantAvailability',
    'BrandRep',
    'SimilarProducts',
    'PairsWithProducts',
    'ConstructorUserContext',
  ],
  requestLinks: [createConstructorRecommendationsLink()],
})

// Only tag pages offer Find Similar and Pairs With.
if (typeof window !== 'undefined' && /\/tag\//.test(window.location.pathname)) {
  prefetchConstructorRecommendations(client)
}

export function Providers({ children }: PropsWithChildren) {
  return (
    <ApolloProvider client={client}>
      <HapticsProvider>{children}</HapticsProvider>
    </ApolloProvider>
  )
}
